import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api/api";
import "./RoadmapAddItem.css";

import SkillModal   from "../../components/SkillModal/SkillModal";
import ProjectModal from "../../components/ProjectModal/ProjectModal";

const STATUS_META = {
  planned:     { icon: "📋", label: "Planned",     color: "#64748b" },
  in_progress: { icon: "⚡", label: "In Progress", color: "#f59e0b" },
  completed:   { icon: "✅", label: "Completed",   color: "#059669" },
};

const extractVideoId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
};

// ✅ Same getThumb as RoadmapCreate — checks item.thumbnail first, then resource URL, then resource-level thumbnail
const getThumb = (item) => {
  if (item?.thumbnail) return item.thumbnail;
  const sources = item?.resources || item?.playlists || [];
  for (const r of sources) {
    const vid = extractVideoId(r?.url || "");
    if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
    if (r?.thumbnail) return r.thumbnail;
  }
  return null;
};

// ✅ Same getResourceCount as RoadmapCreate — existing_course type counts even with empty url
const getResourceCount = (item) => {
  const sources = item?.resources || item?.playlists || [];
  return sources.filter((r) => r?.url?.trim() || r?.type === "existing_course" || r?.courseId).length;
};

// ✅ Detect if skill was added via "My Courses" tab
const isExistingCourse = (item) =>
  !!(item?.existingCourseId || (item?.resources || []).some((r) => r?.type === "existing_course"));

function RoadmapAddItem() {
  const navigate      = useNavigate();
  const { roadmapId } = useParams();

  const [roadmap,  setRoadmap]  = useState(null);
  const [items,    setItems]    = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  const [showSkillModal,   setShowSkillModal]   = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingItem,      setEditingItem]      = useState(null);

  const isDirtyRef    = useRef(false);
  const autoSaveTimer = useRef(null);

  /* ── Load ─────────────────────────────────────────────── */
  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get(`/roadmap/${roadmapId}`);
        setRoadmap(res.data.roadmap);
        setItems(res.data.roadmap?.items || []);
      } catch {
        navigate("/dashboard");
      }
    };
    init();
  }, [navigate, roadmapId]);

  /* ── Core save ───────────────────────────────────────── */
  const saveNow = useCallback(async () => {
    if (!roadmap) return;
    try {
      setSaving(true);
      setSaved(false);
      await api.put(`/roadmap/${roadmapId}`, {
        title:       roadmap.title,
        description: roadmap.description,
        items,
      });
      setSaved(true);
      isDirtyRef.current = false;
      setTimeout(() => setSaved(false), 2000);
    } catch {
      /* silent for autosave */
    } finally {
      setSaving(false);
    }
  }, [roadmapId, roadmap, items]);

  /* ── Autosave (debounced 1.2 s) ──────────────────────── */
  useEffect(() => {
    if (!roadmap) return;
    isDirtyRef.current = true;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(saveNow, 1200);
    return () => clearTimeout(autoSaveTimer.current);
  }, [items, roadmap, saveNow]);

  /* ── Warn on browser close ───────────────────────────── */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  /* ── Done ────────────────────────────────────────────── */
  const handleDone = async () => {
    clearTimeout(autoSaveTimer.current);
    try {
      setSaving(true);
      setSaved(false);
      await api.put(`/roadmap/${roadmapId}`, {
        title:       roadmap.title,
        description: roadmap.description,
        items,
      });
      isDirtyRef.current = false;
      navigate(`/roadmap/${roadmapId}?edit=true`);
    } catch {
      setSaving(false);
      alert("Failed to save. Please try again before leaving.");
    }
  };

  /* ── Edit existing item ──────────────────────────────── */
  const handleEditSave = (updatedData) => {
    setItems((prev) =>
      prev.map((i) =>
        i.order === editingItem.order ? { ...i, ...updatedData } : i
      )
    );
    setEditingItem(null);
  };

  /* ── Delete ──────────────────────────────────────────── */
  const handleDelete = (order) => {
    setItems((prev) =>
      prev
        .filter((i) => i.order !== order)
        .map((i, idx) => ({ ...i, order: idx + 1 }))
    );
  };

  const returnToView = () => {
    if (isDirtyRef.current) {
      const ok = window.confirm("You have unsaved changes. Leave without saving?");
      if (!ok) return;
    }
    navigate(`/roadmap/${roadmapId}?edit=true`);
  };

  const typeIcon = { skill: "🎯", project: "🚀" };
  const sorted   = [...items].sort((a, b) => a.order - b.order);

  if (!roadmap) {
    return (
      <div className="rai-loading">
        <div className="rai-spinner" />
        <p>Loading roadmap…</p>
      </div>
    );
  }

  return (
    <div className="rai-page">
      <div className="rai-container">

        {/* Header */}
        <div className="rai-header">
          <button className="rai-back-btn" onClick={returnToView}>← Back</button>
          <div className="rai-header-main">
            <h1>Add to Roadmap</h1>
            <p className="rai-header-sub">
              Adding items to <strong>{roadmap.title}</strong>
            </p>
          </div>
          {(saving || saved) && (
            <div className={`rai-save-status ${saved ? "rai-save-status--saved" : ""}`}>
              {saving ? "Saving…" : "✓ Saved"}
            </div>
          )}
        </div>

        {/* Meta inputs */}
        <div className="rai-meta-card">
          <div className="rai-field">
            <label className="rai-label">Roadmap Title</label>
            <input
              className="rai-input"
              value={roadmap.title ?? ""}
              onChange={(e) => setRoadmap({ ...roadmap, title: e.target.value })}
            />
          </div>
          <div className="rai-field">
            <label className="rai-label">Description</label>
            <textarea
              className="rai-textarea"
              value={roadmap.description || ""}
              onChange={(e) => setRoadmap({ ...roadmap, description: e.target.value })}
              rows={2}
            />
          </div>
        </div>

        {/* Items list */}
        <div className="rai-section">
          <div className="rai-section-header">
            <h2>Current Items</h2>
            <span className="rai-count">{items.length} item{items.length !== 1 ? "s" : ""}</span>
          </div>

          {items.length === 0 ? (
            <div className="rai-empty">
              No items yet — add your first skill or project below.
            </div>
          ) : (
            <div className="rai-items-list">
              {sorted.map((item, i) => {
                const thumb      = getThumb(item);
                const resCount   = getResourceCount(item);
                const fromCourse = isExistingCourse(item);
                const status     = item.type === "project"
                  ? (STATUS_META[item.status] || STATUS_META.planned)
                  : null;

                return (
                  <div
                    key={item._id || i}
                    className={`rai-item rai-item--${item.type}`}
                    style={{ animationDelay: `${i * 0.05}s` }}
                  >
                    <span className="rai-item-num">{i + 1}</span>

                    {/* ✅ Show thumbnail from item.thumbnail OR YouTube URL */}
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        className="rai-item-thumb"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <span className="rai-item-icon">{typeIcon[item.type] || "📌"}</span>
                    )}

                    <div className="rai-item-info">
                      <span className="rai-item-name">{item.name ?? "Untitled"}</span>
                      <div className="rai-item-meta">
                        <span className="rai-item-type">{item.type}</span>
                        {status && (
                          <span className="rai-item-status" style={{ color: status.color }}>
                            {status.icon} {status.label}
                          </span>
                        )}

                        {/* ✅ Resource count — existing_course shows "1 course", URL-based shows count */}
                        {item.type === "skill" && (
                          <span className="rai-item-resources">
                            {fromCourse
                              ? `📚 1 course${item.totalVideos ? ` (${item.totalVideos} videos)` : ""}`
                              : `📚 ${resCount} resource${resCount !== 1 ? "s" : ""}`}
                          </span>
                        )}

                        {item.type === "project" && Array.isArray(item.techStack) && item.techStack.length > 0 && (
                          <span className="rai-item-tech">
                            {item.techStack.slice(0, 2).join(", ")}
                            {item.techStack.length > 2 ? ` +${item.techStack.length - 2}` : ""}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="rai-item-btns">
                      <button
                        className="rai-icon-btn rai-icon-btn--edit"
                        onClick={() => setEditingItem(item)}
                        title="Edit"
                      >✏</button>
                      <button
                        className="rai-icon-btn rai-icon-btn--delete"
                        onClick={() => handleDelete(item.order)}
                        title="Remove"
                      >✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="rai-actions">
          <button
            className="rai-btn rai-btn--skill"
            onClick={() => setShowSkillModal(true)}
            disabled={saving}
          >
            🎯 Add Skill
          </button>
          <button
            className="rai-btn rai-btn--project"
            onClick={() => setShowProjectModal(true)}
            disabled={saving}
          >
            🚀 Add Project
          </button>
          <button
            className="rai-btn rai-btn--done"
            onClick={handleDone}
            disabled={saving}
            title={saving ? "Saving…" : "Save and return to roadmap"}
          >
            {saving ? "Saving…" : "✓ Done"}
          </button>
        </div>

      </div>

      {/* Edit modals */}
      {editingItem?.type === "skill" && (
        <>
          <div className="modal-backdrop" onClick={() => setEditingItem(null)} />
          <div className="rai-modal-wrapper">
            <SkillModal existing={editingItem} onClose={() => setEditingItem(null)} onSave={handleEditSave} />
          </div>
        </>
      )}
      {editingItem?.type === "project" && (
        <>
          <div className="modal-backdrop" onClick={() => setEditingItem(null)} />
          <div className="rai-modal-wrapper">
            <ProjectModal existing={editingItem} onClose={() => setEditingItem(null)} onSave={handleEditSave} />
          </div>
        </>
      )}

      {/* Add modals */}
      {showSkillModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowSkillModal(false)} />
          <div className="rai-modal-wrapper">
            <SkillModal
              onClose={() => setShowSkillModal(false)}
              onSave={(item) => {
                setItems((prev) => [...prev, { ...item, order: prev.length + 1 }]);
                setShowSkillModal(false);
              }}
            />
          </div>
        </>
      )}
      {showProjectModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowProjectModal(false)} />
          <div className="rai-modal-wrapper">
            <ProjectModal
              onClose={() => setShowProjectModal(false)}
              onSave={(item) => {
                setItems((prev) => [...prev, { ...item, order: prev.length + 1 }]);
                setShowProjectModal(false);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default RoadmapAddItem;